import React, { useState } from "react";
import { Typography, Box, Button, Container, Grid } from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import ReportIcon from "@mui/icons-material/Report";
import PetsIcon from "@mui/icons-material/Pets";
import PolicyIcon from "@mui/icons-material/Policy";
import { colors } from "../../Theme/theme";
import { FIRM } from "../../constants/firm";
import styled from "@emotion/styled";
import { useInView } from "react-intersection-observer";

import CustomizedDialogs from "./EachServiceDialog";
import { serviceDetails } from "./serviceDetails";

const SectionTitle = styled(Typography)(({ theme }) => ({
	color: colors.primary.main,
	fontWeight: 700,
	fontSize: "clamp(2.8rem, 4.5vw, 5rem)",
	letterSpacing: "0.02em",
	textTransform: "none",
	fontFamily: "Georgia, 'Times New Roman', serif",
	lineHeight: 1.2,
	marginBottom: theme.spacing(2),
}));

const SectionTitle2 = styled(Typography)(({ theme }) => ({
	marginBottom: theme.spacing(4),
	color: colors.primary.main,
	fontWeight: 700,
	fontSize: "clamp(1.3rem, 2vw, 2rem)",
	letterSpacing: "0.02em",
	textTransform: "none",
	fontFamily: "Georgia, 'Times New Roman', serif",
	lineHeight: 1.2,
}));

const GoldDivider = styled(Box)(() => ({
	width: "64px",
	height: "4px",
	background: `linear-gradient(90deg, ${colors.accent.main}, ${colors.accent.light})`,
	marginBottom: "28px",
}));

const ServiceCard = styled(Box)(({ theme }) => ({
	position: "relative",
	overflow: "hidden",
	backgroundColor: "#ffffff",
	minHeight: "300px",
	padding: theme.spacing(4),
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	cursor: "pointer",
	transition: "background-color 0.4s ease, box-shadow 0.35s ease, transform 0.25s ease",
	"&::before": {
		content: '""',
		position: "absolute",
		left: 0,
		top: 0,
		width: "4px",
		height: "100%",
		background: colors.primary.main,
		transition: "background 0.4s ease",
		zIndex: 1,
	},
	"&:hover": {
		backgroundColor: colors.primary.darker,
		boxShadow: "0 20px 56px rgba(10, 36, 72, 0.35)",
		transform: "translateY(-3px)",
	},
	"&:hover::before": {
		background: colors.accent.main,
	},
	"&:hover .card-icon": {
		color: colors.accent.main,
		opacity: 0.16,
		transform: "scale(1.08) translate(-6px, -6px)",
	},
	"&:hover .card-number": {
		color: colors.accent.main,
	},
	"&:hover .card-title": {
		color: "#ffffff",
	},
	"&:hover .card-divider": {
		opacity: 0.6,
	},
	"&:hover .card-desc": {
		color: "rgba(255, 255, 255, 0.72)",
	},
	"&:hover .card-cta": {
		color: colors.accent.main,
	},
	"&:hover .card-arrow": {
		opacity: 1,
		transform: "translateX(4px)",
	},
}));

const services = [
	{
		title: "Auto Accidents",
		description:
			"Expert representation for victims of car and truck crashes, seeking maximum compensation for injuries and damages.",
		panel: "panel1",
		Icon: DirectionsCarIcon,
	},
	{
		title: "Premises Liability",
		description:
			"Unsafe properties can cause life-changing injuries in an instant. We'll help you hold property owners accountable for their negligence.",
		panel: "panel2",
		Icon: HomeWorkIcon,
	},
	{
		title: "Medical Malpractice",
		description:
			"When healthcare providers break your trust, the consequences can be devastating. We'll advocate for your rights and pursue the compensation you deserve.",
		panel: "panel3",
		Icon: HealthAndSafetyIcon,
	},
	{
		title: "DUI/DWI Accidents",
		description:
			"When impaired drivers shatter lives, we fight for justice. Let our expertise guide you through recovery while we hold the responsible parties accountable.",
		panel: "panel4",
		Icon: ReportIcon,
	},
	{
		title: "Dog Bites",
		description:
			"Vicious attacks leave more than physical scars. We'll fight for your complete recovery while holding negligent owners responsible.",
		panel: "panel5",
		Icon: PetsIcon,
	},
	{
		title: "Gun Violence",
		description:
			"When senseless gun violence disrupts innocent lives, we stand ready to pursue justice. Let us help you navigate the path to healing and accountability.",
		panel: "panel6",
		Icon: PolicyIcon,
	},
];

const ServicesRefined = () => {
	const [open, setOpen] = useState("");
	const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });

	const handleClickOpen = (panel) => setOpen(panel);
	const handleClose = () => setOpen("");

	return (
		<>
			<Box
				id="Practice"
				ref={ref}
				sx={{
					position: "relative",
					pt: 10,
					pb: 10,
					overflow: "hidden",
					backgroundColor: "#f5f6f8",
					borderTop: { xs: `16px solid ${colors.primary.main}`, md: "none" },
					"&::before": {
						content: '""',
						position: "absolute",
						left: 0,
						top: 0,
						width: "16px",
						height: "100%",
						backgroundColor: colors.primary.main,
					},
					minHeight: "100vh",
				}}
			>
				<Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>

					{/* Section header */}
					<Box sx={{ mb: 8, pl: { xs: 2, md: 2, lg: 4, xl: 8 } }}>
						<motion.div
							initial={{ opacity: 0, y: -20 }}
							animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
							transition={{ duration: 0.8 }}
						>
							<Typography
								sx={{
									fontSize: "0.78rem",
									fontWeight: 700,
									letterSpacing: "0.18em",
									color: colors.primary.main,
									textTransform: "uppercase",
									mb: 2,
									fontFamily: "'Heebo', sans-serif",
								}}
							>
								Legal Services
							</Typography>
							<SectionTitle>Our Practice</SectionTitle>
						</motion.div>

						<motion.div
							initial={{ opacity: 0, scaleX: 0 }}
							animate={inView ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
							transition={{ duration: 0.55, delay: 0.25, ease: "easeOut" }}
							style={{ transformOrigin: "left" }}
						>
							<GoldDivider />
						</motion.div>

						<motion.div
							initial={{ opacity: 0 }}
							animate={inView ? { opacity: 1 } : { opacity: 0 }}
							transition={{ duration: 1, delay: 0.35 }}
						>
							<Typography
								variant="body1"
								sx={{
									maxWidth: 640,
									lineHeight: 1.8,
									fontSize: "1.05rem",
									color: "#4a5568",
									fontFamily: "'Heebo', sans-serif",
								}}
							>
								We provide high-quality legal services to the people of New Mexico. Our firm is
								dedicated to serving clients who have suffered injury due to the negligence or wrongful
								acts of others. As a bilingual firm, we are proud to serve clients who speak both
								English and Spanish.
							</Typography>
						</motion.div>
					</Box>

					{/* Service cards */}
					<Box sx={{ pl: { xs: 2, md: 2, lg: 4, xl: 8 }, pr: { xs: 2, md: 2, lg: 4, xl: 8 } }}>
						<Grid container spacing={3}>
							{services.map((service, index) => {
								const { Icon } = service;
								return (
									<Grid item xs={12} sm={6} md={4} key={service.panel}>
										<motion.div
											initial={{ opacity: 0, y: 40 }}
											animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
											transition={{
												duration: 0.6,
												delay: 0.3 + index * 0.1,
												ease: [0.22, 1, 0.36, 1],
											}}
											style={{ height: "100%" }}
										>
											<ServiceCard onClick={() => handleClickOpen(service.panel)}>

												{/* Icon watermark */}
												<Icon
													className="card-icon"
													sx={{
														position: "absolute",
														bottom: -8,
														right: -8,
														fontSize: "8rem",
														color: colors.primary.main,
														opacity: 0.055,
														transition: "color 0.4s ease, opacity 0.4s ease, transform 0.4s ease",
														pointerEvents: "none",
													}}
												/>

												{/* Top: number */}
												<Box
													className="card-number"
													sx={{
														fontFamily: "'Heebo', sans-serif",
														fontWeight: 700,
														fontSize: "0.72rem",
														letterSpacing: "0.14em",
														color: colors.primary.main,
														transition: "color 0.4s ease",
														mb: 2,
													}}
												>
													{String(index + 1).padStart(2, "0")}
												</Box>

												{/* Middle: title + divider + description */}
												<Box sx={{ flex: 1, zIndex: 1, position: "relative" }}>
													<Typography
														className="card-title"
														component="h3"
														sx={{
															fontFamily: "Georgia, 'Times New Roman', serif",
															fontWeight: 700,
															fontSize: "clamp(1.15rem, 1.6vw, 1.35rem)",
															color: colors.primary.main,
															lineHeight: 1.25,
															mb: 1.5,
															transition: "color 0.4s ease",
														}}
													>
														{service.title}
													</Typography>

													<Box
														className="card-divider"
														sx={{
															width: 32,
															height: 3,
															background: `linear-gradient(90deg, ${colors.accent.main}, ${colors.accent.light})`,
															mb: 2,
															transition: "opacity 0.4s ease",
														}}
													/>

													<Typography
														className="card-desc"
														sx={{
															fontSize: "0.9rem",
															lineHeight: 1.7,
															color: "#4a5568",
															fontFamily: "'Heebo', sans-serif",
															transition: "color 0.4s ease",
														}}
													>
														{service.description}
													</Typography>
												</Box>

												{/* Bottom: CTA */}
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 0.75,
														mt: 3,
														zIndex: 1,
														position: "relative",
													}}
												>
													<Typography
														className="card-cta"
														sx={{
															fontSize: "0.72rem",
															fontWeight: 700,
															letterSpacing: "0.14em",
															textTransform: "uppercase",
															color: colors.primary.main,
															fontFamily: "'Heebo', sans-serif",
															transition: "color 0.4s ease",
														}}
													>
														Learn More
													</Typography>
													<ArrowForwardIcon
														className="card-arrow"
														sx={{
															fontSize: "0.8rem",
															color: colors.accent.main,
															opacity: 0,
															transform: "translateX(-4px)",
															transition: "opacity 0.3s ease, transform 0.3s ease",
														}}
													/>
												</Box>
											</ServiceCard>
										</motion.div>
									</Grid>
								);
							})}
						</Grid>
					</Box>

					{/* CTA */}
					<Box sx={{ mt: 10, py: 4, textAlign: "center" }}>
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
							transition={{ duration: 0.8, delay: 0.9 }}
						>
							<SectionTitle2>
								Don't Navigate the Legal System Alone!
								<br />
								Hablamos Español
							</SectionTitle2>
							<Button
								variant="contained"
								href={FIRM.phone.href}
								sx={{
									backgroundColor: colors.primary.main,
									color: "#fff",
									px: 4,
									py: 1.5,
									fontSize: { xs: "0.82rem", sm: "0.92rem" },
									fontWeight: 700,
									borderRadius: 0,
									letterSpacing: "0.08em",
									textTransform: "uppercase",
									lineHeight: 1.4,
									boxShadow: "0 4px 16px rgba(26, 71, 137, 0.3)",
									"&:hover": {
										backgroundColor: colors.primary.dark,
										boxShadow: "0 6px 20px rgba(26, 71, 137, 0.4)",
										transform: "translateY(-1px)",
									},
									transition: "all 0.2s ease",
								}}
							>
								Free Consultation<br />{FIRM.phone.display}
							</Button>
						</motion.div>
					</Box>
				</Container>

				{CustomizedDialogs && (
					<CustomizedDialogs handleClickOpen={handleClickOpen} handleClose={handleClose} open={open} />
				)}
			</Box>
		</>
	);
};

export default ServicesRefined;
